import { createRng } from "../rng";
import {
	type BaseState,
	type GameDefinition,
	type MoveOutcome,
	otherPlayer,
	type PlayerIndex,
	reject,
	resultFromScores,
} from "../types";
import { RANKS, rankLabel } from "./cards";

/** Counts how many of each rank a hand holds. */
function countOf(hand: number[], rank: number): number {
	return hand.filter((card) => card === rank).length;
}

// --- Go Fish --------------------------------------------------------------

export const GO_FISH_HAND = 7;

export interface GoFishState extends BaseState {
	kind: "gofish";
	/** Both hands; the opponent's is emptied before being sent. */
	hands: [number[], number[]];
	counts: [number, number];
	/** The pool left to fish from — hidden, only its size is public. */
	pool: number[];
	poolLeft: number;
	/** Completed sets of four, per player. */
	books: [number[], number[]];
	lastEvent: string | null;
}

export interface GoFishMove {
	rank: number;
}

/** Moves any completed set of four out of the hand and into the books. */
function layDownBooks(hand: number[], books: number[]): { hand: number[]; books: number[] } {
	const nextBooks = [...books];
	let nextHand = [...hand];
	for (const rank of RANKS) {
		if (countOf(nextHand, rank) === 4) {
			nextBooks.push(rank);
			nextHand = nextHand.filter((card) => card !== rank);
		}
	}
	return { hand: nextHand, books: nextBooks };
}

export const goFish: GameDefinition<GoFishState, GoFishMove> = {
	id: "gofish",
	category: "cards",
	title: "Go Fish",
	emoji: "🎣",
	blurb: "Ask for the cards you need. Collect four of a kind to score.",
	howTo: [
		"Ask your friend for a card you already hold one of.",
		"If they have any, they hand them all over and you go again.",
		"If not — go fish! Take one from the pool.",
		"Four of a kind is a set. Most sets wins.",
	],

	create(rng) {
		const deck: number[] = [];
		for (const rank of RANKS) for (let i = 0; i < 4; i++) deck.push(rank);
		const shuffled = rng.shuffle(deck);

		const first = layDownBooks(shuffled.slice(0, GO_FISH_HAND), []);
		const second = layDownBooks(shuffled.slice(GO_FISH_HAND, GO_FISH_HAND * 2), []);
		const pool = shuffled.slice(GO_FISH_HAND * 2);

		return {
			kind: "gofish",
			hands: [first.hand, second.hand],
			counts: [first.hand.length, second.hand.length],
			pool,
			poolLeft: pool.length,
			books: [first.books, second.books],
			lastEvent: null,
			turn: 0,
			scores: [first.books.length, second.books.length],
			result: null,
		};
	},

	parseMove(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		const rank = (raw as { rank?: unknown }).rank;
		if (typeof rank !== "number" || !Number.isInteger(rank)) return null;
		if (!(RANKS as readonly number[]).includes(rank)) return null;
		return { rank };
	},

	apply(state, player, move): MoveOutcome<GoFishState> {
		if (state.result) return reject("This game is already finished.");
		if (state.turn !== player) return reject("It's not your turn yet.");
		// You may only ask for a rank you already hold — the classic rule.
		if (countOf(state.hands[player], move.rank) === 0) {
			return reject("You can only ask for a card you already have.");
		}

		const them = otherPlayer(player);
		const hands: [number[], number[]] = [[...state.hands[0]], [...state.hands[1]]];
		const books: [number[], number[]] = [[...state.books[0]], [...state.books[1]]];
		const pool = [...state.pool];
		const given = countOf(hands[them], move.rank);
		let goAgain = false;
		let lastEvent: string;

		if (given > 0) {
			hands[them] = hands[them].filter((card) => card !== move.rank);
			for (let i = 0; i < given; i++) hands[player].push(move.rank);
			goAgain = true;
			lastEvent = `Handed over ${given} × ${rankLabel(move.rank)}!`;
		} else {
			const drawn = pool.pop();
			if (drawn !== undefined) {
				hands[player].push(drawn);
				// Fishing exactly what you asked for earns another go.
				goAgain = drawn === move.rank;
				lastEvent = goAgain ? `Go fish — and caught the ${rankLabel(move.rank)}!` : "Go fish!";
			} else {
				lastEvent = "Go fish — but the pool is empty.";
			}
		}

		for (const seat of [0, 1] as const) {
			const tidied = layDownBooks(hands[seat], books[seat]);
			hands[seat] = tidied.hand;
			books[seat] = tidied.books;
			// An empty hand draws again while there is still a pool — otherwise
			// the player has nothing to ask for and the game just stops.
			while (hands[seat].length === 0 && pool.length > 0) {
				const card = pool.pop();
				if (card === undefined) break;
				hands[seat].push(card);
				const refilled = layDownBooks(hands[seat], books[seat]);
				hands[seat] = refilled.hand;
				books[seat] = refilled.books;
			}
		}

		const scores: [number, number] = [books[0].length, books[1].length];
		// It is over when the cards run out anywhere they can.
		const finished =
			books[0].length + books[1].length === RANKS.length ||
			(pool.length === 0 && (hands[0].length === 0 || hands[1].length === 0));

		return {
			ok: true,
			state: {
				...state,
				hands,
				counts: [hands[0].length, hands[1].length],
				pool,
				poolLeft: pool.length,
				books,
				scores,
				lastEvent,
				turn: finished ? null : goAgain ? player : them,
				result: finished ? resultFromScores(scores) : null,
			},
		};
	},

	legalMoves(state, player) {
		if (state.result || state.turn !== player) return [];
		// One ask per rank you hold — asking twice for the same thing is the same move.
		return [...new Set(state.hands[player])].map((rank) => ({ rank }));
	},

	tick() {
		return null;
	},

	nextTickAt() {
		return null;
	},

	redact(state, viewer) {
		const them = otherPlayer(viewer);
		const hands: [number[], number[]] = [[], []];
		hands[viewer] = state.hands[viewer];
		// Their hand is theirs; you only know how big it is.
		hands[them] = [];
		return { ...state, hands, pool: [] };
	},
};

// --- Old Maid -------------------------------------------------------------

/** The card nobody wants: it has no partner. */
export const OLD_MAID = 99;
const OLD_MAID_RANKS = [2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

export interface OldMaidState extends BaseState {
	kind: "oldmaid";
	hands: [number[], number[]];
	counts: [number, number];
	/** Pairs each player has thrown away. */
	discarded: [number, number];
	/**
	 * Seeds the re-shuffle of a hand after every take, and never leaves the
	 * server. Hands used to keep insertion order — the taken card always went
	 * to the back, and the joker never pairs away, so it sat at the last index
	 * for the rest of the game. `redact` masks values but not positions, which
	 * made "never take the rightmost card" a guaranteed win.
	 */
	shuffleSeed: number;
	/** How many takes so far, so each re-shuffle draws a fresh order. */
	takes: number;
	lastEvent: string | null;
}

export interface OldMaidMove {
	/** Which card of their hand to take, by position. */
	index: number;
}

function discardPairs(hand: number[]): { hand: number[]; pairs: number } {
	const kept: number[] = [];
	let pairs = 0;
	const seen = new Map<number, number>();
	for (const card of hand) seen.set(card, (seen.get(card) ?? 0) + 1);
	for (const [card, count] of seen) {
		pairs += Math.floor(count / 2);
		// An odd one out stays in the hand.
		if (count % 2 === 1) kept.push(card);
	}
	return { hand: kept, pairs };
}

export const oldMaid: GameDefinition<OldMaidState, OldMaidMove> = {
	id: "oldmaid",
	category: "cards",
	title: "Old Maid Duel",
	emoji: "🃏",
	blurb: "Take a card, make a pair, throw it away. Don't be left with the joker!",
	howTo: [
		"Pairs are thrown away straight away.",
		"On your turn take one of your friend's cards — you can't see which is which.",
		"If it makes a pair, that pair goes too.",
		"Empty your hand first. Whoever is left holding the joker loses.",
	],

	create(rng) {
		const deck: number[] = [];
		for (const rank of OLD_MAID_RANKS) for (let i = 0; i < 2; i++) deck.push(rank);
		deck.push(OLD_MAID);
		const shuffled = rng.shuffle(deck);

		const half = Math.ceil(shuffled.length / 2);
		const first = discardPairs(shuffled.slice(0, half));
		const second = discardPairs(shuffled.slice(half));

		return {
			kind: "oldmaid",
			hands: [rng.shuffle(first.hand), rng.shuffle(second.hand)],
			counts: [first.hand.length, second.hand.length],
			discarded: [first.pairs, second.pairs],
			shuffleSeed: rng.int(0x7fffffff),
			takes: 0,
			lastEvent: null,
			turn: 0,
			scores: [first.pairs, second.pairs],
			result: null,
		};
	},

	parseMove(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		const index = (raw as { index?: unknown }).index;
		if (typeof index !== "number" || !Number.isInteger(index) || index < 0) return null;
		return { index };
	},

	apply(state, player, move): MoveOutcome<OldMaidState> {
		if (state.result) return reject("This game is already finished.");
		if (state.turn !== player) return reject("It's not your turn yet.");

		const them = otherPlayer(player);
		const theirHand = [...state.hands[them]];
		if (move.index >= theirHand.length) return reject("They don't have that many cards.");

		const taken = theirHand.splice(move.index, 1)[0];
		const mine = [...state.hands[player], taken];
		const tidied = discardPairs(mine);
		const takes = state.takes + 1;

		const hands: [number[], number[]] = [[], []];
		// Re-shuffle the taker's hand so where a card *sits* says nothing about
		// when it arrived. The opponent picks by position, so position must
		// carry no information.
		hands[player] = createRng((state.shuffleSeed + takes) >>> 0).shuffle(tidied.hand);
		hands[them] = theirHand;
		// However many cards left the hand, half of them were a pair.
		const pairsThisTurn = Math.floor((mine.length - tidied.hand.length) / 2);
		const discarded: [number, number] = [...state.discarded];
		discarded[player] = state.discarded[player] + pairsThisTurn;

		const emptied = hands[player].length === 0 || hands[them].length === 0;
		const scores: [number, number] = [discarded[0], discarded[1]];

		let result: { winner: PlayerIndex | null } | null = null;
		if (emptied) {
			// Whoever still holds cards is holding the joker.
			result = { winner: hands[player].length === 0 ? player : them };
		}

		return {
			ok: true,
			state: {
				...state,
				hands,
				counts: [hands[0].length, hands[1].length],
				discarded,
				takes,
				scores,
				lastEvent:
					pairsThisTurn > 0
						? `Took a ${taken === OLD_MAID ? "joker" : rankLabel(taken)} and made a pair!`
						: `Took a card — no pair this time.`,
				turn: emptied ? null : them,
				result,
			},
		};
	},

	legalMoves(state, player) {
		if (state.result || state.turn !== player) return [];
		const them = otherPlayer(player);
		return state.hands[them].map((_, index) => ({ index }));
	},

	tick() {
		return null;
	},

	nextTickAt() {
		return null;
	},

	redact(state, viewer) {
		const them = otherPlayer(viewer);
		const hands: [number[], number[]] = [[], []];
		hands[viewer] = state.hands[viewer];
		// You are supposed to be picking blind.
		hands[them] = state.hands[them].map(() => 0);
		// The seed would let a client replay every re-shuffle.
		return { ...state, hands, shuffleSeed: 0 };
	},
};
